import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Loader2, RefreshCw, ScanLine, CalendarClock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { csvDownload } from './_shared';

const INR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const BUCKETS = [
  { key: 'expired', label: 'Expired', cls: 'bg-red-600 text-white border-red-700' },
  { key: '7', label: '≤ 7 days', cls: 'bg-red-100 text-red-800 border-red-200' },
  { key: '15', label: '≤ 15 days', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  { key: '30', label: '≤ 30 days', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { key: '60', label: '≤ 60 days', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { key: '90', label: '≤ 90 days', cls: 'bg-lime-100 text-lime-800 border-lime-200' },
  { key: '120', label: '≤ 120 days', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { key: '180', label: '≤ 180 days', cls: 'bg-teal-100 text-teal-800 border-teal-200' },
  { key: 'unknown', label: 'No expiry data', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
];
const rowBadge = (days) => {
  if (days === null || days === undefined) return { label: 'No expiry recorded', cls: 'bg-gray-200 text-gray-700' };
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, cls: 'bg-red-600 text-white' };
  if (days <= 30) return { label: `${days} days`, cls: 'bg-red-100 text-red-700' };
  if (days <= 90) return { label: `${days} days`, cls: 'bg-amber-100 text-amber-700' };
  return { label: `${days} days`, cls: 'bg-emerald-100 text-emerald-700' };
};

// "≤ N days" buckets are cumulative (everything expiring within N days, excluding already-expired)
const cumulative = (summary, key) => {
  if (!summary) return { items: 0, quantity: 0, stock_value: 0 };
  if (key === 'expired' || key === 'unknown') return summary[key] || { items: 0, quantity: 0, stock_value: 0 };
  const limit = Number(key);
  return BUCKETS.filter(b => !Number.isNaN(Number(b.key)) && Number(b.key) <= limit)
    .reduce((acc, b) => ({ items: acc.items + (summary[b.key]?.items || 0), quantity: acc.quantity + (summary[b.key]?.quantity || 0), stock_value: acc.stock_value + (summary[b.key]?.stock_value || 0) }), { items: 0, quantity: 0, stock_value: 0 });
};

const ExpiryReport = ({ scope = 'hq' }) => {
  const { toast } = useToast();
  const [location, setLocation] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bucket, setBucket] = useState('90');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    if (scope !== 'hq') return;
    api.get('/stores').then(r => setStores(r.data?.stores || r.data || [])).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data?.warehouses || r.data || [])).catch(() => {});
  }, [scope]);
  // typing a product search widens the view to every row of that product (any window, incl. no-expiry rows)
  useEffect(() => { const t = setTimeout(() => { const v = qInput.trim(); setQ(v); if (v) setBucket('all'); }, 350); return () => clearTimeout(t); }, [qInput]);
  useEffect(() => { setLocationId('all'); }, [location]);

  const load = async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const params = { bucket };
      if (q) params.q = q;
      if (scope === 'hq') { params.location = location; if (locationId !== 'all') params.location_id = locationId; }
      const res = await api.get('/product-reports/expiry', { params });
      if (myReq !== reqId.current) return; // a newer request has superseded this one
      setData(res.data);
    } catch (err) {
      toast({ title: 'Load failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { if (myReq === reqId.current) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [location, locationId, bucket, q]);

  const rows = data?.rows || [];
  const summary = data?.summary;
  const csvRows = useMemo(() => rows.map(r => ({ Product: r.name, SKU: r.sku, Batch: r.batch_number || '', Expiry: r.expiry_date, 'Days left': r.days_left, Qty: r.quantity, 'Stock value': r.stock_value, Location: `${r.location_code || ''} ${r.location_name}`.trim(), Type: r.location_type })), [rows]);
  const locList = location === 'warehouse' ? warehouses : stores;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="expiry-report">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><CalendarClock className="w-6 h-6 text-red-600" /> Stock Expiry Report</h1>
          <p className="text-gray-500 text-sm">Stock in hand by expiry window — expired, and expiring within 7 / 15 / 30 / 60 / 90 / 120 / 180 days</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="expiry-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => csvDownload(csvRows, `expiry_${bucket}.csv`)} data-testid="expiry-csv"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>

      {/* Bucket tiles double as the filter */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2 mb-4">
        {BUCKETS.map(b => {
          const c = cumulative(summary, b.key);
          const active = bucket === b.key;
          return (
            <button key={b.key} type="button" onClick={() => setBucket(b.key)}
              className={`rounded-lg border p-3 text-left transition ${b.cls} ${active ? 'ring-2 ring-offset-1 ring-gray-900' : 'opacity-60 hover:opacity-100'}`} data-testid={`expiry-bucket-${b.key}`}>
              <p className="text-[11px] uppercase tracking-wide font-semibold">{b.label}</p>
              <p className="text-xl font-bold">{c.items}</p>
              <p className="text-[11px]">{c.quantity} pcs · {INR(c.stock_value)}</p>
            </button>
          );
        })}
      </div>

      <Card className="bg-white border-gray-200 mb-4"><CardContent className="p-4 flex flex-wrap items-end gap-3">
        {scope === 'hq' && (
          <>
            <div className="min-w-[10rem]"><label className="block text-xs text-gray-500 mb-1">Location type</label>
              <Select value={location} onValueChange={setLocation}><SelectTrigger data-testid="expiry-location-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Stores + Warehouses</SelectItem><SelectItem value="store">Stores</SelectItem><SelectItem value="warehouse">Warehouses</SelectItem></SelectContent></Select></div>
            {location !== 'all' && (
              <div className="min-w-[14rem]"><label className="block text-xs text-gray-500 mb-1">{location === 'store' ? 'Store' : 'Warehouse'}</label>
                <Select value={locationId} onValueChange={setLocationId}><SelectTrigger data-testid="expiry-location"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{locList.map(s => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}</SelectContent></Select></div>
            )}
          </>
        )}
        <div className="flex-1 min-w-[16rem]"><label className="block text-xs text-gray-500 mb-1">Product (scan barcode / name / SKU)</label>
          <div className="relative"><ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Scan or type…" className="pl-10" data-testid="expiry-search" /></div></div>
        <Button variant={bucket === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setBucket('all')} data-testid="expiry-bucket-all">All stock</Button>
      </CardContent></Card>

      {data && data.unknown_expiry_rows > 0 && (
        <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm" data-testid="expiry-unknown-note">
          <AlertTriangle className="w-4 h-4 text-gray-400 shrink-0" />
          <p className="text-gray-600 flex-1">
            <span className="font-semibold text-gray-800">{data.unknown_expiry_rows} stock row(s) have no expiry date</span> — received without an expiry on the GRN, so they cannot appear in any expiry window.
            {scope === 'hq' ? ' Expiry is now carried batch-wise from warehouse to store on every new transfer; use the button to fill older rows from known GRN batches of the same product.' : ' Ask HQ to fill missing expiry dates.'}
          </p>
          {scope === 'hq' && (
            <Button size="sm" variant="outline" disabled={backfilling} onClick={async () => {
              setBackfilling(true);
              try { const r = await api.post('/product-reports/expiry/backfill'); toast({ title: 'Expiry filled', description: `${r.data.updated} stock row(s) updated from known batches` }); load(); }
              catch (err) { toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' }); }
              finally { setBackfilling(false); }
            }} data-testid="expiry-backfill-btn">{backfilling ? 'Filling…' : 'Fill from GRN batches'}</Button>
          )}
        </div>
      )}

      <Card className="bg-white border-gray-200"><CardContent className="p-0">
        {loading && !data ? <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div> : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No stock in this expiry window</div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-gray-50">
              <TableHead>Product</TableHead><TableHead>Batch</TableHead><TableHead>Expiry</TableHead><TableHead>Days left</TableHead>
              {scope === 'hq' && <TableHead>Location</TableHead>}
              <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">MRP</TableHead><TableHead className="text-right">Stock value</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => { const b = rowBadge(r.days_left); return (
                <TableRow key={r.entry_id} className="hover:bg-gray-50" data-testid={`expiry-row-${r.entry_id}`}>
                  <TableCell><p className="font-medium text-gray-800">{r.name}</p><p className="text-xs text-gray-500 font-mono">{r.sku}{r.is_sample ? ' · SAMPLE' : ''}</p></TableCell>
                  <TableCell className="text-sm text-gray-600">{r.batch_number || '—'}</TableCell>
                  <TableCell className="text-sm">{r.expiry_date || '—'}{r.expiry_inferred && <span className="ml-1 text-[10px] text-gray-400" title="Filled from GRN batch of this product">(inferred)</span>}</TableCell>
                  <TableCell><Badge className={`${b.cls} border-0`}>{r.days_left < 0 && <AlertTriangle className="w-3 h-3 mr-1" />}{b.label}</Badge></TableCell>
                  {scope === 'hq' && <TableCell className="text-sm"><span className="font-mono text-xs text-gray-500 mr-1">{r.location_code}</span>{r.location_name}<span className="ml-1 text-[10px] uppercase text-gray-400">{r.location_type}</span></TableCell>}
                  <TableCell className="text-right font-semibold">{r.quantity}</TableCell>
                  <TableCell className="text-right">{r.mrp != null ? INR(r.mrp) : '—'}</TableCell>
                  <TableCell className="text-right">{INR(r.stock_value)}</TableCell>
                </TableRow>
              ); })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
};

export default ExpiryReport;
